/**
 * Hold skjermen våken.
 *
 * Primært: Screen Wake Lock API (iOS 16.4+, Android Chrome, Safari 17+).
 * Fallback: en skjult, loopende (lydløs) video matet fra en canvas-stream —
 * mange nettlesere holder skjermen våken så lenge en video spilles. Dette
 * dekker enheter som mangler Wake Lock API, eller der API-et finnes men ikke
 * faktisk hindrer at skjermen slukker (enkelte Linux-telefoner).
 *
 * Begge brukes samtidig for maks robusthet. Videoen er lydløs og 1×1 px, så
 * den forstyrrer verken lyd eller visning.
 */

type WakeLockSentinelCompat = {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
};

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: {
    request(type: 'screen'): Promise<WakeLockSentinelCompat>;
  };
};

let sentinel: WakeLockSentinelCompat | null = null;
let desired = false;
let visibilityHandler: (() => void) | null = null;

// Video-fallback
let video: HTMLVideoElement | null = null;
let drawTimer: number | null = null;

export function isWakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
}

function captureStreamSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const c = document.createElement('canvas') as HTMLCanvasElement & {
    captureStream?: unknown;
  };
  return typeof c.captureStream === 'function';
}

/** Finnes det i det hele tatt en måte å holde skjermen våken på? */
export function isScreenAwakeAvailable(): boolean {
  return isWakeLockSupported() || captureStreamSupported();
}

async function acquire(): Promise<void> {
  const nav = navigator as NavigatorWithWakeLock;
  if (!nav.wakeLock) return;
  try {
    const s = await nav.wakeLock.request('screen');
    s.addEventListener('release', () => {
      sentinel = null;
    });
    sentinel = s;
  } catch (err) {
    console.warn('Wake Lock nektet:', err);
  }
}

function startVideoFallback(): void {
  if (video) return;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 2;
    const ctx = canvas.getContext('2d');
    const c = canvas as HTMLCanvasElement & {
      captureStream?: (fps?: number) => MediaStream;
    };
    if (typeof c.captureStream !== 'function') return;
    const stream = c.captureStream(2);

    const v = document.createElement('video');
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    (v as HTMLVideoElement & { playsInline?: boolean }).playsInline = true;
    v.srcObject = stream;
    v.style.cssText =
      'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(v);

    // Tegn jevnlig så streamen holdes «levende».
    let toggle = false;
    drawTimer = window.setInterval(() => {
      if (!ctx) return;
      toggle = !toggle;
      ctx.fillStyle = toggle ? '#000001' : '#000000';
      ctx.fillRect(0, 0, 2, 2);
    }, 1000);

    void v.play().catch(() => {
      /* autoplay nektet — lydløs video bør likevel være tillatt */
    });
    video = v;
  } catch {
    /* fallback ikke tilgjengelig */
  }
}

function stopVideoFallback(): void {
  if (drawTimer !== null) {
    clearInterval(drawTimer);
    drawTimer = null;
  }
  if (video) {
    try {
      video.pause();
      const s = video.srcObject as MediaStream | null;
      s?.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
      video.remove();
    } catch {
      /* ignore */
    }
    video = null;
  }
}

/** Slå på skjerm-på (API + video-fallback). Idempotent. */
export async function enableWakeLock(): Promise<boolean> {
  desired = true;

  if (isWakeLockSupported()) {
    if (!visibilityHandler) {
      visibilityHandler = async () => {
        if (desired && document.visibilityState === 'visible' && !sentinel) {
          await acquire();
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    }
    if (document.visibilityState === 'visible' && !sentinel) {
      await acquire();
    }
  }

  // Video-fallback i tillegg — robust på enheter uten/med svakt API.
  startVideoFallback();

  return isWakeLockActive();
}

/** Slå av skjerm-på og slipp alt. */
export async function disableWakeLock(): Promise<void> {
  desired = false;
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  if (sentinel && !sentinel.released) {
    try {
      await sentinel.release();
    } catch {
      /* noop */
    }
  }
  sentinel = null;
  stopVideoFallback();
}

/** Holdes skjermen våken akkurat nå (via API eller video)? */
export function isWakeLockActive(): boolean {
  const apiActive = !!sentinel && !sentinel.released;
  const videoActive = !!video && !video.paused;
  return apiActive || videoActive;
}
